import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "src/Components/Common/Loader";
import UserService from "src/Services/API/user.service";
import PasswordUpgradeService from "src/Services/API/password-upgrade.service";
import { handleError, PASSWORD_HINT, validatePassword } from "src/Common/function";

function PasswordField({ label, value, onChange, error }) {
  return (
    <div className="w-full mb-3">
      <p className="text-[12px] font-OpenSansSemiBold text-inputEmailTitle mb-1">{label}</p>
      <div
        className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${
          error ? "border-red-500" : ""
        }`}
      >
        <input
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-inputEmailTitle text-[11px] font-OpenSansRegular"
        />
      </div>
    </div>
  );
}

// Shown after a sign-in whose password predates the password rules (see
// PasswordUpgradeService), and it can't be dismissed: the app stays blocked
// until the password has been changed. Right after sign-in it uses the
// password just entered; only when that is gone (a reload) does it ask for it.
function WeakPasswordModal() {
  const [shown, setShown] = useState(false);
  const [signInPassword, setSignInPassword] = useState(() => PasswordUpgradeService.getSignInPassword());
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (PasswordUpgradeService.isDue(UserService.getCurrentUser()?.id)) {
      setShown(true);
    }
  }, []);

  const changeClicked = async () => {
    const current = signInPassword ?? currentPassword;
    const result = validatePassword(newPassword);
    const nextErrors = {
      current: !current,
      new: !result.success,
      confirm: newPassword !== confirmPassword,
    };
    setErrors(nextErrors);
    if (nextErrors.current) return toast("Please enter your current password");
    if (nextErrors.new) return toast(result.message);
    if (nextErrors.confirm) return toast("Passwords don't match");

    setSaving(true);
    try {
      await PasswordUpgradeService.changePassword(current, newPassword);
      toast("Password changed");
      PasswordUpgradeService.clear();
      setShown(false);
    } catch (error) {
      // e.g. a new password the server finds too common. Should it reject the
      // current password, ask for it instead of retrying the remembered one.
      if (/current password/i.test(error?.response?.data?.message ?? "")) {
        setSignInPassword(null);
      }
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  if (!shown) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen flex items-center justify-center z-50 bg-black/50">
      <div className="w-[440px] px-5 py-5 bg-white flex flex-col rounded-[6px] shadow-2xs">
        <p className="text-[16px] font-OpenSansSemiBold text-titleColor mb-2 text-center">
          CHOOSE A STRONGER PASSWORD
        </p>
        <p className="text-[12px] font-OpenSansRegular text-subTitleColor mb-4 text-center">
          Your password is easy to guess. Set a new one to continue. {PASSWORD_HINT}
        </p>

        {signInPassword == null && (
          <PasswordField
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            error={errors.current}
          />
        )}
        <PasswordField label="New password" value={newPassword} onChange={setNewPassword} error={errors.new} />
        <PasswordField
          label="Confirm new password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={errors.confirm}
        />

        <div className="flex items-center justify-center mt-2">
          <div
            onClick={saving ? undefined : changeClicked}
            className={`w-full h-[30px] text-[10px] rounded-[6px] flex items-center justify-center bg-blueColor text-white ${
              saving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            {saving ? <Loader variant="button" ariaLabel="changing-password" /> : "CHANGE PASSWORD"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeakPasswordModal;
